import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CalendarDays, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";

const FOLLOW_UP_DAYS = 90;

const FollowUpReminder = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showReminder, setShowReminder] = useState(false);
  const [daysSince, setDaysSince] = useState(0);

  useEffect(() => {
    if (!user) return;

    const check = async () => {
      // Get last completed consultation
      const { data } = await supabase
        .from("appointments")
        .select("start_time")
        .eq("patient_id", user.id)
        .eq("status", "completed")
        .order("start_time", { ascending: false })
        .limit(1);

      if (!data || data.length === 0) return;

      const lastDate = new Date(data[0].start_time);
      const now = new Date();
      const diff = Math.floor((now.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));
      setDaysSince(diff);

      // Also check if there's already a scheduled appointment
      const { data: upcoming } = await supabase
        .from("appointments")
        .select("id")
        .eq("patient_id", user.id)
        .eq("status", "scheduled")
        .limit(1);

      if (diff >= FOLLOW_UP_DAYS && (!upcoming || upcoming.length === 0)) {
        setShowReminder(true);
      }
    };

    check();
  }, [user]);

  if (!showReminder) return null;

  return (
    <Card className="border-amber-300/50 bg-amber-50/50 mb-6">
      <CardContent className="p-5 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
            <Clock size={18} className="text-amber-600" />
          </div>
          <div>
            <p className="text-sm font-semibold text-amber-900">Follow-Up Recommended</p>
            <p className="text-xs text-amber-700">
              It's been {daysSince} days since your last consultation. Schedule a follow-up so your provider can review your progress and refill your prescription.
            </p>
          </div>
        </div>
        <Button size="sm" onClick={() => navigate("/book")} className="gap-2">
          <CalendarDays size={14} /> Book Follow-Up
        </Button>
      </CardContent>
    </Card>
  );
};

export default FollowUpReminder;
